import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Fixed booking bar for phones and tablets (below the `lg` breakpoint, where the booking card sits at the very end
 * of the page): the price the page already shows, and a « Réserver » button that opens the EXISTING booking form —
 * it clicks the page's own button (`targetTestId`), so the same dialog, tracking and rules apply; no new flow.
 *
 * Hidden while:
 *  - the page's own booking button is on screen (never two booking buttons at once),
 *  - any dialog is open (booking form, calendar, sign-in prompt, conditions…).
 * Respects the iPhone home-bar safe area. A spacer keeps the end of the page (footer) reachable.
 *
 * Props: `label` (small text above the price), `price` (already formatted, or null/'' when it is not known: a neutral
 * wording is shown instead — never an invented price), `unit` (e.g. « THB / jour »), `targetTestId`.
 */
export const MobileBookingBar = ({ label, price, unit, targetTestId }) => {
  const { t } = useTranslation();
  const [targetVisible, setTargetVisible] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const target = document.querySelector(`[data-testid="${targetTestId}"]`);
    if (!target || typeof IntersectionObserver === 'undefined') { setMounted(true); return undefined; }
    const io = new IntersectionObserver(([entry]) => setTargetVisible(entry.isIntersecting), { threshold: 0.1 });
    io.observe(target);
    setMounted(true);
    return () => io.disconnect();
  }, [targetTestId]);

  useEffect(() => {
    const check = () => setDialogOpen(Boolean(document.querySelector('[role="dialog"], [role="alertdialog"]')));
    check();
    const mo = new MutationObserver(check);
    mo.observe(document.body, { childList: true, subtree: false });
    return () => mo.disconnect();
  }, []);

  const open = () => {
    const target = document.querySelector(`[data-testid="${targetTestId}"]`);
    if (target) target.click();
  };

  const visible = mounted && !targetVisible && !dialogOpen;
  const hasPrice = price !== null && price !== undefined && String(price).trim() !== '';

  return (
    <>
      <div className="lg:hidden h-[calc(5rem+env(safe-area-inset-bottom))]" aria-hidden="true" />
      <div
        className={`lg:hidden fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 backdrop-blur shadow-[0_-4px_16px_rgba(15,23,42,0.08)] transition-transform duration-200 ${visible ? 'translate-y-0' : 'translate-y-full pointer-events-none'}`}
        style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))', paddingLeft: 'max(1rem, env(safe-area-inset-left))', paddingRight: 'max(1rem, env(safe-area-inset-right))' }}
        data-testid="mobile-booking-bar"
        data-visible={visible ? 'true' : 'false'}
        aria-hidden={visible ? undefined : 'true'}
      >
        <div className="flex items-center justify-between gap-3 pt-3">
          <div className="min-w-0" data-testid="mobile-booking-bar-price">
            {hasPrice ? (
              <>
                {label && <p className="text-[11px] leading-tight text-ink-muted truncate">{label}</p>}
                <p className="text-primary leading-tight truncate">
                  <span className="text-xl font-bold">{price}</span>
                  {unit && <span className="ml-1 text-sm font-medium text-ink-light">{unit}</span>}
                </p>
              </>
            ) : (
              <p className="text-sm font-medium text-ink-light leading-snug">{t('common.mobileBar.priceDepends')}</p>
            )}
          </div>
          <button
            type="button"
            onClick={open}
            tabIndex={visible ? 0 : -1}
            className="btn-primary shrink-0 min-h-12 px-7 text-base"
            data-testid="mobile-booking-bar-button"
          >
            {t('common.mobileBar.book')}
          </button>
        </div>
      </div>
    </>
  );
};

export default MobileBookingBar;
